import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, StepList, RichFAQ, RelatedLinks, RichCTA,
} from '../RichBlocks';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

/**
 * Ladeboks installation pris — bespoke rich guide.
 * Prices from ENKEL_PRODUCTS.installation (enkel's own pricing).
 * Technical requirements grounded in Danish electrical regulations
 * (autoriseret elinstallatør required; standard 1-fase/3-fase, fejlstrømsrelæ).
 */
export default function LadeboksInstallationPris({ page }) {
  const inst = ENKEL_PRODUCTS.installation;
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    {
      question: 'Hvad koster det at installere en ladeboks?',
      answer: `Hos enkel energi starter installation fra ${inst.existing.price.toLocaleString('da-DK')} kr., hvis du har et egnet eksisterende stik/forberedt installation, og fra ${inst.standard.price.toLocaleString('da-DK')} kr. for en standardinstallation. Den endelige pris afhænger af afstand til eltavle, kabelføring og om tavlen skal opdateres. Alle installationer udføres af en autoriseret elinstallatør.`,
    },
    {
      question: 'Hvad er inkluderet i en standardinstallation?',
      answer: 'En standardinstallation dækker typisk montering af ladeboksen, en fast kabelføring over en kortere afstand fra eltavlen, nødvendigt fejlstrømsrelæ/afbryder og idriftsættelse, så boksen er klar til brug. Lange kabeltræk, gravearbejde, tavleopgradering eller ekstra grupper kan koste ekstra.',
    },
    {
      question: 'Hvorfor skal en autoriseret elinstallatør gøre det?',
      answer: 'I Danmark må fast tilslutning af en ladeboks kun udføres af en autoriseret elinstallatør. Det sikrer korrekt jordforbindelse, fejlstrømsbeskyttelse og overholdelse af stærkstrømsbekendtgørelsen. En forkert installation kan være farlig og kan ugyldiggøre forsikring og garanti.',
    },
    {
      question: 'Skal jeg have 3-faset strøm for at lade med 22 kW?',
      answer: 'Ja. For at udnytte 22 kW kræves en 3-faset installation. Har du kun 1-faset (typisk i ældre boliger), lader boksen langsommere (op til ca. 3,7–7,4 kW). En elinstallatør kan vurdere din tavle og rådgive om en eventuel opgradering.',
    },
    {
      question: 'Kan jeg få elafgift refunderet efter installation?',
      answer: 'Ja. Når ladeboksen har en godkendt måler og er tilsluttet en operatør, kan elafgiften på din hjemmeopladning refunderes via den danske ordning. Hos enkel energi håndteres dette automatisk via serviceaftalen til 0 kr./md.',
    },
    {
      question: 'Hvor lang tid tager en installation?',
      answer: 'En typisk standardinstallation tager 1–3 timer. Kræver det længere kabeltræk, tavleopgradering eller gravearbejde, kan det tage længere – det aftales på forhånd med elinstallatøren.',
    },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'priser', label: 'Priser' },
    { id: 'paavirker', label: 'Hvad påvirker prisen' },
    { id: 'forloeb', label: 'Sådan foregår det' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Ladebokse', path: '/opladere' }, { name: 'Installation pris', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Hvad koster installation af ladeboks?" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="Guide · Installation · 2026"
        h1="Hvad koster det at installere en ladeboks?"
        intro="Få et klart overblik over installationspriser, hvad der er inkluderet, og hvad der kan koste ekstra. Alle installationer udføres af en autoriseret elinstallatør."
        badges={[`Fra ${inst.existing.price.toLocaleString('da-DK')} kr.`, 'Autoriseret elinstallatør', '1–3 timer typisk']}
        updated={updated}
        image="https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: `${inst.existing.price.toLocaleString('da-DK')} kr.`, label: 'Eksisterende stik – fra' },
          { value: `${inst.standard.price.toLocaleString('da-DK')} kr.`, label: 'Standard – fra' },
          { value: '22 kW', label: 'kræver 3-faset' },
          { value: 'Refusion', label: 'elafgift automatisk' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Hvad koster installation – kort svar">
        <AnswerBox question="Hvad koster det at installere en ladeboks?">
          Hos enkel energi starter installation fra <strong>{inst.existing.price.toLocaleString('da-DK')} kr.</strong> ved et egnet eksisterende
          stik og fra <strong>{inst.standard.price.toLocaleString('da-DK')} kr.</strong> for en standardinstallation. Prisen afhænger af afstand
          til eltavlen, kabelføring og om tavlen skal opdateres. Arbejdet udføres altid af en <strong>autoriseret elinstallatør</strong>.
        </AnswerBox>
      </ProseSection>

      <ProseSection id="priser" heading="Installationspriser" alt>
        <div className="my-2">
          <ComparisonTable
            caption="Installationspriser for ladeboks"
            columns={[{ name: 'Eksisterende stik' }, { name: 'Standard', highlight: true }]}
            rows={[
              { label: 'Pris fra', values: [`${inst.existing.price.toLocaleString('da-DK')} kr.`, `${inst.standard.price.toLocaleString('da-DK')} kr.`] },
              { label: 'Montering af ladeboks', values: [true, true] },
              { label: 'Kortere kabelføring', values: [true, true] },
              { label: 'Fejlstrømsbeskyttelse', values: [true, true] },
              { label: 'Idriftsættelse', values: [true, true] },
              { label: 'Egnet forberedt stik', values: ['Påkrævet', 'Ikke påkrævet'] },
              { label: 'Autoriseret elinstallatør', values: [true, true] },
            ]}
          />
        </div>
        <p className="text-xs text-gray-500 mt-3">Priser er enkel energis vejledende fra-priser. Endelig pris bekræftes efter vurdering af din installation.</p>
      </ProseSection>

      <ProseSection id="paavirker" heading="Hvad påvirker installationsprisen?">
        <FactGrid columns={2} facts={[
          { value: 'Afstand', label: 'Eltavle → ladeboks', sub: 'Længere kabeltræk koster mere' },
          { value: 'Tavle', label: 'Plads og kapacitet', sub: 'Opgradering kan være nødvendig' },
          { value: 'Faser', label: '1-faset vs 3-faset', sub: '22 kW kræver 3-faset' },
          { value: 'Føringsvej', label: 'Mur, kabelbakke, gravning', sub: 'Synlig vs skjult føring' },
        ]} />
        <p className="mt-6">
          De fleste villaer med en moderne eltavle og kort afstand til monteringsstedet kan klares som en standardinstallation. Bor du i en
          ældre bolig med 1-faset forsyning eller en fyldt tavle, kan der komme ekstra omkostninger til opgradering. Elinstallatøren vurderer
          dette på forhånd, så du kender prisen, før arbejdet går i gang.
        </p>
      </ProseSection>

      <ProseSection id="forloeb" heading="Sådan foregår installationen" alt>
        <StepList steps={[
          { title: 'Vurdering af din installation', body: 'Elinstallatøren gennemgår din eltavle, afstand og faser – og bekræfter prisen.' },
          { title: 'Montering af ladeboks', body: 'Boksen monteres sikkert på væggen, og kablet føres fra eltavlen.' },
          { title: 'Fejlstrømsbeskyttelse', body: 'Korrekt afbryder/fejlstrømsrelæ installeres i henhold til reglerne.' },
          { title: 'Idriftsættelse og test', body: 'Boksen sættes i drift, testes og tilsluttes enkel Saving Mode.' },
        ]} />
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/opladere', label: 'Se ladebokse' },
        { path: '/hvilken-ladeboks-skal-jeg-vaelge', label: 'Hvilken ladeboks skal jeg vælge?' },
        { path: '/nexblue-edge-2', label: 'NexBlue Edge 2' },
        { path: '/zaptec-go', label: 'Zaptec Go' },
        { path: '/service-aftale', label: 'Serviceaftale 0 kr./md.' },
        { path: '/el-fordele#laderabat', label: 'Laderabat – 10 øre/kWh' },
      ]} />

      <RichCTA
        headline="Klar til at få en ladeboks?"
        subtext="Vælg ladeboks og installation samlet – Saving Mode, serviceaftale og laderabat er inkluderet til 0 kr./md."
        primary={{ path: '/opladere', text: 'Se ladebokse' }}
        secondary={{ path: '/hvilken-ladeboks-skal-jeg-vaelge', text: 'Køberguide' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}